import clsx from 'clsx'
import React, {useContext, useMemo} from 'react'

import {Grid} from '../../grid'
import {PlusIcon} from '../../icons'
import {Popover} from '../../legacy/popover'
import {Link} from '../../link'
import {useTheme} from '../../theme'
import {Buttonv2 as Button} from '../../v2'
import {List} from '../list'
import {PassportContext} from './context'
import {useStyles} from './styles'
import {AuthorizedUserProps} from './types'
import UserItem from './UserItem'

const ITEM_HEIGHT = 54

const AuthorizedUser: React.FC<AuthorizedUserProps> = ({defaultUser, users}) => {
  const classes = useStyles()
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget)
  }
  const theme = useTheme()
  const handleClose = () => {
    setAnchorEl(null)
  }
  const {avatarUrl, canAddMore, signoutHref, addUserHref, mobile, maxDisplayedUsers, locale} =
    useContext(PassportContext)
  const open = Boolean(anchorEl)
  const usersWithoutDefault = useMemo(
    () => users.filter(user => user.login !== defaultUser.login),
    [users, defaultUser.login],
  )
  const maxListHeight =
    (ITEM_HEIGHT + theme.spacing(0.5)) * usersWithoutDefault.slice(0, maxDisplayedUsers).filter(Boolean).length +
    theme.spacing(2.5)
  const id = open ? 'auth-popover' : undefined

  return (
    <>
      <div className={classes.root}>
        <Link aria-label={defaultUser.login} className={classes.pointer} onClick={handleClick}>
          <Grid className={classes.auth} spacing={2} wrap="nowrap" container>
            {!mobile && (
              <Grid item>
                <div className={classes.login}>{defaultUser.login}</div>
              </Grid>
            )}
            <Grid className={classes.flexCenter} item>
              <div className={classes.image}>
                <img
                  alt=""
                  className={classes.avatar}
                  height="42"
                  src={`${avatarUrl}/islands-middle`}
                  srcSet={`${avatarUrl}/islands-middle 1x, ${avatarUrl}/islands-retina-middle 2x`}
                  width="42"
                />
              </div>
            </Grid>
          </Grid>
        </Link>
      </div>
      <Popover
        anchorEl={anchorEl}
        id={id}
        onClose={handleClose}
        open={open}
        PaperProps={{classes: {root: classes.paper}}}
        transformOrigin={{horizontal: -10, vertical: -70}}
      >
        <div className={classes.divider}>
          <UserItem item={defaultUser} isCurrent />
        </div>
        {usersWithoutDefault.length > 0 && (
          <div className={clsx(classes.divider, classes.noBottomPadding)} style={{height: maxListHeight}}>
            <List
              getId={item => item.email}
              items={usersWithoutDefault}
              renderItem={item => <UserItem item={item} />}
            />
          </div>
        )}
        <div className={clsx(classes.divider, users.length > maxDisplayedUsers && classes.overflowShadow)}>
          <Grid direction="column" size="s" container>
            {canAddMore && (
              <Grid item>
                <Button
                  className={classes.noWrap}
                  Component={Link}
                  href={addUserHref}
                  lead={<PlusIcon />}
                  variant="outline"
                >
                  {locale.addUser}
                </Button>
              </Grid>
            )}
            <Grid item>
              <Button Component={Link} href={signoutHref}>
                {locale.signout}
              </Button>
            </Grid>
          </Grid>
        </div>
      </Popover>
    </>
  )
}

export default AuthorizedUser
